import {createSlice, createSelector, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk, RootState} from "../../app/store";
import {RecordModel,
        RecordFactory,
        LayoutRelatedListModel} from "../../models/models";
import {SortDirectionType,
        SortOrderJSON,
        SortOrderModel,
        SortOrderFactory} from "../../models/modelsUserConfiguration";
import {SearchTermModel,
        SearchTermJSON,
        SearchTermFactory,
        PaginatorModel,
        PaginatorJSON,
        PaginatorFactory,
        RelatedListConfigurationJSON,
        RelatedListConfigurationModel,
        RelatedListConfigurationFactory} from "../../models/modelsListConfiguration";
import Config from "../../config";

interface RelatedListState {
    isFetching: boolean;
    records: RecordModel[];
    listConfig: RelatedListConfigurationModel;
    error: string | null;
}

interface RelatedListsState {
  [key: string]: RelatedListState
}
const initialState: RelatedListsState = {};

const createInitialListState = (): RelatedListState => (
  {
    isFetching: false,
    records: [] as RecordModel[],
    listConfig: new RelatedListConfigurationFactory().init(),
    error: null
  }
)

const sortFunction = (recordA: RecordModel,
                      recordB: RecordModel,
                      sortOrder: SortOrderModel) => {

  const fieldApiName = sortOrder.fieldApiName
  const sortDirection = sortOrder.sortDirection

  const fieldA = recordA.fields[fieldApiName]//recordA.fields.find((thisField) => thisField.fieldName === fieldApiName);
  const fieldB = recordB.fields[fieldApiName]//recordB.fields.find((thisField) => thisField.fieldName === fieldApiName);

  let sortIndex = 1

  if(sortDirection === 'asc'){
    fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=1 : sortIndex=-1
  }
  if(sortDirection === 'desc'){
    fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=-1 : sortIndex=1
  }

  return sortIndex

}


export const relatedRecordsSlice = createSlice({
  name: "relatedRecords",
  initialState,
  reducers: {
    storeFetching: (state, action: PayloadAction<{layoutRelatedListUuid: string, isFetching: boolean}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      if(!state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid] = createInitialListState();
      }
      state[layoutRelatedListUuid].isFetching = action.payload.isFetching;
    },
    storeRelatedRecords: (state, action: PayloadAction<{layoutRelatedListUuid: string, records: RecordModel[]}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      if(!state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid] = createInitialListState();
      }
      const newRecords = action.payload.records.map((record) => record);
      let allRecords = state[layoutRelatedListUuid].records;
      allRecords = allRecords.concat(newRecords)
      state[layoutRelatedListUuid].records = allRecords.map((record) => record)
    },
    storeRelatedListConfig: (state, action: PayloadAction<{layoutRelatedListUuid: string, listConfig: RelatedListConfigurationModel}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      if(!state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid] = createInitialListState();
      }

      let loadedPages: number[] = [];
      if(state[layoutRelatedListUuid].listConfig){
        const relatedPaginator = state[layoutRelatedListUuid].listConfig.paginator
        loadedPages = [...relatedPaginator.loadedPages, action.payload.listConfig.paginator.currentPage];
      }
      state[layoutRelatedListUuid].listConfig = action.payload.listConfig;
      state[layoutRelatedListUuid].listConfig.paginator.loadedPages=loadedPages;
    },
    storePaginator: (state, action: PayloadAction<{layoutRelatedListUuid: string, paginator: PaginatorModel}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      if(!state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid] = createInitialListState();
      }

      let loadedPages: number[] = [];
      const relatedPaginator = state[layoutRelatedListUuid].listConfig.paginator
      loadedPages = [...relatedPaginator.loadedPages, action.payload.paginator.currentPage];

      //NOTE: need to re-create complete config to trigger re-render
      const newConfig = {...state[layoutRelatedListUuid].listConfig,
                         paginator: action.payload.paginator}
      newConfig.paginator.loadedPages=loadedPages;
      state[layoutRelatedListUuid].listConfig=newConfig;
    },
    storePageLoading: (state, action: PayloadAction<{layoutRelatedListUuid: string, pageNumber: number}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      const pageNumber = action.payload.pageNumber;
      if(!state[layoutRelatedListUuid]) return;

      const thisPaginator = state[layoutRelatedListUuid].listConfig.paginator
      const pagesLoading = [...thisPaginator.loadingPages, pageNumber];
      state[layoutRelatedListUuid].listConfig.paginator.loadingPages = pagesLoading;
    },
    storePageLoaded: (state, action: PayloadAction<{layoutRelatedListUuid: string, pageNumber: number}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      const pageNumber = action.payload.pageNumber;
      if(!state[layoutRelatedListUuid]) return;

      const thisPaginator = state[layoutRelatedListUuid].listConfig.paginator
      const pagesLoaded = [...thisPaginator.loadedPages, pageNumber];
      const pagesLoading = thisPaginator.loadingPages.filter((thisPage) => thisPage != pageNumber);
      state[layoutRelatedListUuid].listConfig.paginator.loadedPages = pagesLoaded;
      state[layoutRelatedListUuid].listConfig.paginator.loadingPages = pagesLoading;
    },
    removePageLoading: (state, action: PayloadAction<{layoutRelatedListUuid: string, pageNumber: number}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      const pageNumber = action.payload.pageNumber;
      if(!state[layoutRelatedListUuid]) return;

      const thisPaginator = state[layoutRelatedListUuid].listConfig.paginator
      const pagesLoading = thisPaginator.loadingPages.filter((thisPage) => thisPage != pageNumber);
      state[layoutRelatedListUuid].listConfig.paginator.loadingPages = pagesLoading;
    },
    storeSortOrders: (state, action: PayloadAction<{layoutRelatedListUuid: string, sortOrders: SortOrderModel[]}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      const newConfig = {...state[layoutRelatedListUuid].listConfig,
                         sortOrders: action.payload.sortOrders};
      newConfig.paginator.currentPage=0
      state[layoutRelatedListUuid].listConfig = newConfig;
    },
    storeSearchTerm: (state, action: PayloadAction<{layoutRelatedListUuid: string, searchTerm: SearchTermModel}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      const newConfig = {...state[layoutRelatedListUuid].listConfig,
                         searchTerm: action.payload.searchTerm};
      newConfig.paginator.currentPage=0
      state[layoutRelatedListUuid].listConfig = newConfig;
    },
    storeError: (state, action: PayloadAction<string | null>) => {
//      state.error = action.payload;
    },
    purgeLayoutRelatedList: (state, action: PayloadAction<string>) => {
      //remove only the current key (uuid of current layout related list);
      const layoutRelatedListUuid = action.payload;
      delete state.layoutRelatedListUuid;
//      state.error = null;
    },
    purgePaginator: (state, action: PayloadAction<string>) => {
      //input: layoutRelatedListUuid as string
      const layoutRelatedListUuid = action.payload
      if(state[layoutRelatedListUuid]){
        const newPaginator = new PaginatorFactory().init()
        state[layoutRelatedListUuid].listConfig.paginator = newPaginator;
      }
    },
    purgeSearchTerm: (state, action: PayloadAction<string>) => {
      //input: layoutRelatedListUuid as string
      const layoutRelatedListUuid = action.payload
      const newSearchTerm = new SearchTermFactory().init()
      if(state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid].listConfig.searchTerm = newSearchTerm;
      }
    },
    purgeRecords: (state, action: PayloadAction<string>) => {
      //input: layoutRelatedListUuid as string
      const layoutRelatedListUuid = action.payload;
      if(state[layoutRelatedListUuid]){
        state[layoutRelatedListUuid].records = [] as RecordModel[];
      }
    },
    purgeStore: (state) => {
      state = {};
    },
  },
});



export const { storeRelatedRecords,
               storeRelatedListConfig,
               storePaginator,
               storePageLoading,
               storePageLoaded,
               removePageLoading,
               storeSortOrders,
               storeSearchTerm,
               storeFetching,
               storeError,
               purgePaginator,
               purgeSearchTerm,
               purgeRecords,
               purgeLayoutRelatedList,
               purgeStore } = relatedRecordsSlice.actions;


export const retrieveRelatedRecords =
  (objectApiName: string | null,
   recordUuid: string | null,
   layoutRelatedList: LayoutRelatedListModel,
   relatedListConfig: RelatedListConfigurationModel | null,
   accessToken: string,
   refetch: boolean,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}/${recordUuid}/related`;

    const headers = new Headers();

    interface RequestBody {
      layout_related_list_uuid: string,
      list_config?: RelatedListConfigurationJSON
    }
    const requestBody: RequestBody = {
      layout_related_list_uuid: layoutRelatedList.Uuid_sys
    };

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    if(relatedListConfig){
      const thisJSON = new RelatedListConfigurationFactory().toJSON(relatedListConfig)
      requestBody.list_config = thisJSON;
    }


    try {

      dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                              isFetching: true}));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching related records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                isFetching: false}));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data.records || !Array.isArray(data.records)){
          dispatch(storeError("Error when fetching records"));
          dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                  isFetching: false}));
          return;
      }

      if(!data.list_config){
          dispatch(storeError("Error when fetching list configuration"));
          dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                  isFetching: false}));
          return;
      }

      dispatch(
        storeRelatedRecords({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                             records: data.records.map((record: any) => new RecordFactory().fromJSON(record))}
                           )
      )

      const thisRelatedListConfig = new RelatedListConfigurationFactory().fromJSON(data.list_config)
      dispatch(
        storeRelatedListConfig({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                listConfig: thisRelatedListConfig})
      )

      //prefetch records
      dispatch(prefetchRelatedRecords(objectApiName,
                                      recordUuid,
                                      layoutRelatedList,
                                      thisRelatedListConfig,
                                      accessToken))

      dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                              isFetching: false}));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                              isFetching: false}));
      console.error(error);
    }
  };


export const prefetchRelatedRecords =
  (objectApiName: string | null,
   recordUuid: string | null,
   layoutRelatedList: LayoutRelatedListModel,
   relatedListConfig: RelatedListConfigurationModel,
   accessToken: string): AppThunk =>
  async (dispatch) => {

    const thisPaginator = relatedListConfig.paginator;
    const pageNumber = thisPaginator.nextPage;

    //nothing to be done, because end of list already found
    if(thisPaginator.currentPage === pageNumber) return;

    //nothing to be done, because page already loaded
    if(thisPaginator.loadedPages.includes(pageNumber)) return;

    //nothing to be done, because page is currently already being loaded
    if(thisPaginator.loadingPages.includes(pageNumber)) return;

    //now, set paginator.currentPage to the nextPage
    //and load stuff
    const newRelatedListConfig = {...relatedListConfig,
                                  paginator: {...relatedListConfig.paginator,
                                              currentPage: pageNumber}}

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}/${recordUuid}/related`;

    const headers = new Headers();

    interface RequestBody {
      layout_related_list_uuid: string,
      list_config: RelatedListConfigurationJSON
    }
    const thisJSON = new RelatedListConfigurationFactory().toJSON(newRelatedListConfig)
    const requestBody: RequestBody = {
      layout_related_list_uuid: layoutRelatedList.Uuid_sys,
      list_config: thisJSON
    };

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    try {
      dispatch(storePageLoading({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                pageNumber: pageNumber}));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401) return;
      if (!response.ok) return;

      const data = await response.json();

      if(!data.records || !Array.isArray(data.records)) return;
      if(!data.list_config) return;

      dispatch(
        storeRelatedRecords({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                             records: data.records.map((record: any) => new RecordFactory().fromJSON(record))}
                           )
      )

      //store actually loaded page from listview_config
      const thisRelatedListConfig = new RelatedListConfigurationFactory().fromJSON(data.list_config);
      const loadedPageNumber = thisRelatedListConfig.paginator.currentPage;
      dispatch(storePageLoaded({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                pageNumber: loadedPageNumber}));

    } catch (error) {
      dispatch(removePageLoading({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                  pageNumber: pageNumber}));
      console.error(error);
    }
  };

/*
export const selectIsFetching = (state: RootState) =>
  state.relatedRecordsStore.isFetching;

export const selectError = (state: RootState) =>
  state.relatedRecordsStore.error;

export const selectRelatedRecords = (state: RootState) =>{
    return state.relatedRecordsStore.relatedRecords;
}

export const selectRelatedPaginator = (state: RootState) =>{
    return state.relatedRecordsStore.relatedListConfig.paginator;
}

export const selectRelatedListConfig = (state: RootState) =>{
    return state.relatedRecordsStore.relatedListConfig;
}
*/

export const selectListState = (state: RootState, relatedList: LayoutRelatedListModel) => {
  if(!state.relatedRecordsStore[relatedList.Uuid_sys]) return createInitialListState()
  return state.relatedRecordsStore[relatedList.Uuid_sys]
}

export const selectRecords = (state: RootState, relatedList: LayoutRelatedListModel) => {
  if(!state.relatedRecordsStore[relatedList.Uuid_sys]) return [] as RecordModel[]

  const listConfig = state.relatedRecordsStore[relatedList.Uuid_sys].listConfig;
  const records = state.relatedRecordsStore[relatedList.Uuid_sys].records;
  let cloneRecords = [...records];
  if(listConfig.sortOrders.length > 0){
    const thisSortOrder = listConfig.sortOrders[0]
      cloneRecords =  cloneRecords.sort((a: RecordModel, b: RecordModel) => sortFunction(a,b,thisSortOrder));
  }

  if(listConfig.searchTerm.searchText !== ''){
    const filterTypes = ["string", "number"]

    cloneRecords = cloneRecords.filter((thisRecord) => {
      const keys = Object.keys(thisRecord.fields);
      const fieldValuesFound = keys.filter((thisKey) => {
        const thisField = thisRecord.fields[thisKey];
        if(filterTypes.includes(typeof(thisField.fieldValue)) &&
           thisField.fieldValue.toString().toLowerCase().includes(listConfig.searchTerm.searchText)){
             return 1
           }
         })
      if(fieldValuesFound.length > 0){
        return thisRecord
      }
    })
  }

  return cloneRecords
}

/*
export const selectListState = (relatedList: LayoutRelatedListModel) => createSelector(
  [
    (state: RootState) => state.relatedRecordsStore[relatedList.Uuid_sys] ?? createInitialListState()
  ],
  (listState) => {
    //check if records need sorting
    return listState
  }
)
*/
/*






export const selectVisibleRecords = (relatedList: LayoutRelatedListModel) => createSelector(
  [
    (state: RootState) => state[relatedList.Uuid_sys].records ?? [] as RecordModel[],
    (state: RootState) => state[relatedList.Uuid_sys].listConfig ?? new RelatedListConfigurationFactory().init()
  ],
  (records, listConfig) => {
    //check if records need sorting
    console.log('awflakf')
    if(listConfig.sortOrders.length > 0){
      const thisSortOrder = listConfig.sortOrders[0]
      const cloneRecords = [...records]
      return cloneRecords.sort((a: RecordModel, b: RecordModel) => sortFunction(a,b,thisSortOrder));
    }

    return records
  }
)
*/

export default relatedRecordsSlice.reducer;
